// @flow

import { API } from "aws-amplify";
import React, { useState, useEffect, useRef } from "react";
import { onCreateMessageBySession } from "./../graphql/subscriptions";
import { messagesBySession } from "./../graphql/queries";
import { mergeHistory, fetchRecentMessages, useBackfill } from "./../utils/MessageBackfill";
import { subscribeAppSync } from "./../utils/AppSyncSubscription";
import { Container, Row, Col } from "react-bootstrap";
import Card from "react-bootstrap/Card";

type Props = $ReadOnly<{
  sessionId: string,
  title: ?string,
}>;

const MAX_MESSAGES_COUNT_IN_HISTORY = 8;

const styles = {
  index: {
    display: "inline",
    float: "left",
    width: "7%",
    // marginLeft: "0px",
    // paddingLeft: "0px"
  },
  message: {
    display: "inline",
    float: "left",
    paddingLeft: "10px",
    width: "93%",
  },
  empty: {
    display: "inline",
    float: "left",
    width: "7%",
  },
};

// Only fetches the latest 20 messages
async function fetchMessages(sessionId) {
  const apiData = await API.graphql({
    query: messagesBySession,
    variables: {
      sessionMessagesId: sessionId,
      sortDirection: "DESC",
      limit: MAX_MESSAGES_COUNT_IN_HISTORY,
    },
  });
  return apiData.data?.messagesBySession?.items.reverse() ?? [];
}

export default function MessagesView(props: Props): React$Element<any> {
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    async function initialFetch() {
      const messages = await fetchMessages(props.sessionId);
      // 불러오는 사이 구독으로 먼저 들어온 메시지와 id 기준으로 합친다
      setMessages((prev) =>
        mergeHistory(messages, prev, MAX_MESSAGES_COUNT_IN_HISTORY)
      );
    }
    initialFetch();
  }, [props.sessionId]);

  // useEffect(() => {
  //   const subscription = API.graphql({
  //     query: onCreateMessageBySession,
  //     variables: {
  //       sessionMessagesId: props.sessionId,
  //     },
  //   }).subscribe({
  //     next: ({ provider, value }) => {
  //       if (value.data.onCreateMessageBySession.text === "") {
  //         return;
  //       } else if (value.data.onCreateMessageBySession.text === " ") {
  //         return;
  //       } else {
  //         setMessages((prevMessages) => [
  //           ...prevMessages.slice((MAX_MESSAGES_COUNT_IN_HISTORY - 1) * -1),
  //           value.data.onCreateMessageBySession,
  //         ]);
  //       }
  //     },
  //     error: (error) => console.warn(error),
  //   });
  //   return () => {
  //     subscription.unsubscribe();
  //   };
  // }, []);

  // 구독이 놓친 메시지를 (재)구독 직후·탭 복귀 시 조회로 채운다 (#182)
  const scheduleBackfill = useBackfill(async () => {
    try {
      const recent = await fetchRecentMessages(
        props.sessionId,
        MAX_MESSAGES_COUNT_IN_HISTORY
      );
      setMessages((prev) =>
        mergeHistory(prev, recent, MAX_MESSAGES_COUNT_IN_HISTORY)
      );
    } catch (e) {
      console.warn("[MessagesView] backfill failed", e);
    }
  }, [props.sessionId]);

  // 끊기면 이유와 상관없이 다시 구독하고, 그때마다 놓친 메시지를 채운다 (#201)
  useEffect(() => {
    const subscription = subscribeAppSync(
      {
        query: onCreateMessageBySession,
        variables: { sessionMessagesId: props.sessionId },
      },
      {
        context: "MessagesView",
        next: ({ value }) => {
          const msg = value?.data?.onCreateMessageBySession;
          if (!msg) return;

          // 기존 로직 유지: 빈/공백 메시지 무시
          if (msg.text === "" || msg.text === " " || msg.text === "　") return;

          setMessages((prevMessages) =>
            mergeHistory(prevMessages, [msg], MAX_MESSAGES_COUNT_IN_HISTORY)
          );
        },
        onResubscribed: scheduleBackfill,
      }
    );
    scheduleBackfill();

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sessionId]);

  return (
    <Card>
      {props.title != null ? <Card.Header as="h5">History</Card.Header> : null}
      <Card.Body className="p-1">
        <Container
          className="m-0"
          style={{
            height: "23vh",
            // maxHeight: "30vh",
            overflow: "hidden",
          }}
        >
          <Row>
            <Col className="p-0">
              {messages != null && messages.length > 0
                ? messages.map((message, index) => (
                    <Row className="p-0" key={message.id}>
                      <Col>
                        {index > messages.length - 2 ? (
                          // <b>
                          //   <div style={styles.index}>{message.scriptIndex}</div>
                          //   <div style={styles.message}>{message.text}</div>
                          // </b>
                          <b>
                            {message.scriptIndex ? (
                              <div style={styles.index}>
                                {message.scriptIndex}
                              </div>
                            ) : (
                              <div style={styles.empty}>--</div>
                            )}
                            <div style={styles.message}>{message.text}</div>
                          </b>
                        ) : (
                          <div>
                            {message.scriptIndex ? (
                              <div style={styles.index}>
                                {message.scriptIndex}
                              </div>
                            ) : (
                              <div style={styles.empty}>--</div>
                            )}
                            <div style={styles.message}>{message.text}</div>
                          </div>
                        )}
                      </Col>
                    </Row>
                  ))
                : null}
              <div ref={messagesEndRef} />
            </Col>
          </Row>
        </Container>
      </Card.Body>
    </Card>
  );
}
